import { useForm, Head, router, Link } from '@inertiajs/react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import ImageUpload from '@/components/ImageUpload'
import GoogleOAuthButton from '@/components/GoogleOAuthButton'
import { TagCombobox } from '@/components/TagCombobox'
import type { FormEvent } from 'react'
import type { SettingsUser } from '@/types'

type PostEmailFrequency = 'per_post' | 'weekly_digest' | 'off'

const POST_EMAIL_OPTIONS: Array<{
  value: PostEmailFrequency
  title: string
  description: string
}> = [
  {
    value: 'per_post',
    title: 'Email me about every post',
    description: 'Get an email whenever an organization you follow shares a new update.',
  },
  {
    value: 'weekly_digest',
    title: 'Send me a weekly digest',
    description: 'One email each Tuesday summarizing posts from organizations you follow.',
  },
  {
    value: 'off',
    title: "Don't email me",
    description: 'New posts still appear in your feed — you just won’t get emails about them.',
  },
]

type SettingsProps = {
  user: SettingsUser
  cause_suggestions: string[]
  network_suggestions: string[]
  selection_limit: number
  custom_entry_limit: number
}

export default function Settings({
  user,
  cause_suggestions: causeSuggestions,
  network_suggestions: networkSuggestions,
  selection_limit: selectionLimit,
  custom_entry_limit: customEntryLimit,
}: SettingsProps) {
  const { data, setData, patch, processing, errors, transform } = useForm({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    username: user.username,
    bio: user.bio ?? '',
    birthdate: user.birthdate ?? '',
    sex: user.sex ?? '',
    city: user.city ?? '',
    state: user.state ?? '',
    social_x: user.social_x ?? '',
    social_linkedin: user.social_linkedin ?? '',
    social_instagram: user.social_instagram ?? '',
    social_facebook: user.social_facebook ?? '',
    interested_causes: user.interested_causes ?? [],
    interested_industries: user.interested_industries ?? [],
    visibility: user.visibility,
    visibility_full_name: user.visibility_full_name,
    visibility_email: user.visibility_email,
    business: user.business,
    post_email_frequency: user.post_email_frequency,
    password: '',
    password_confirmation: '',
    avatar: '' as string,
    remove_avatar: false,
    banner: '' as string,
    remove_banner: false,
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    transform(formData => ({ user: formData }))
    patch('/settings')
  }

  return (
    <>
      <Head title="Settings" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="mb-8 text-4xl font-bold tracking-tight">Settings</h1>

        <form onSubmit={submit} className="space-y-6">
          {/* Banner */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Banner</CardTitle>
              <CardDescription>A wide cover image displayed at the top of your profile</CardDescription>
            </CardHeader>
            <CardContent>
              <ImageUpload
                currentUrl={user.banner_url}
                onUpload={(signedId) => {
                  if (signedId) {
                    setData('banner', signedId)
                    setData('remove_banner', false)
                  } else {
                    setData('banner', '')
                    setData('remove_banner', true)
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Info</CardTitle>
              <CardDescription>Your name and account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                <div className="mx-auto w-32 shrink-0 sm:mx-0">
                  <ImageUpload
                    currentUrl={user.avatar_url}
                    variant="circle"
                    onUpload={(signedId) => {
                      if (signedId) {
                        setData('avatar', signedId)
                        setData('remove_avatar', false)
                      } else {
                        setData('avatar', '')
                        setData('remove_avatar', true)
                      }
                    }}
                  />
                  <p className="mt-1.5 text-center text-xs text-muted-foreground">Profile photo</p>
                </div>
                <div className="min-w-0 flex-1 space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First name</Label>
                      <Input
                        id="first_name"
                        value={data.first_name}
                        onChange={e => setData('first_name', e.target.value)}
                      />
                      {errors.first_name && (
                        <p className="text-base text-destructive">{errors.first_name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last name</Label>
                      <Input
                        id="last_name"
                        value={data.last_name}
                        onChange={e => setData('last_name', e.target.value)}
                      />
                      {errors.last_name && (
                        <p className="text-base text-destructive">{errors.last_name}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={data.email}
                      onChange={e => setData('email', e.target.value)}
                    />
                    {errors.email && (
                      <p className="text-base text-destructive">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      value={data.username}
                      onChange={e => setData('username', e.target.value)}
                    />
                    {errors.username && (
                      <p className="text-base text-destructive">{errors.username}</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile */}
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Tell others about yourself</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={data.bio}
                  onChange={e => setData('bio', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birthdate">Birthdate</Label>
                  <Input
                    id="birthdate"
                    type="date"
                    value={data.birthdate}
                    onChange={e => setData('birthdate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sex">Sex</Label>
                  <select
                    id="sex"
                    value={data.sex}
                    onChange={e => setData('sex', e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">Prefer not to say</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={data.city}
                    onChange={e => setData('city', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={data.state}
                    onChange={e => setData('state', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Links */}
          <Card>
            <CardHeader>
              <CardTitle>Social Links</CardTitle>
              <CardDescription>Your social media profiles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="social_x">X (Twitter)</Label>
                <Input
                  id="social_x"
                  value={data.social_x}
                  onChange={e => setData('social_x', e.target.value)}
                  placeholder="@handle or URL"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_linkedin">LinkedIn</Label>
                <Input
                  id="social_linkedin"
                  value={data.social_linkedin}
                  onChange={e => setData('social_linkedin', e.target.value)}
                  placeholder="username or URL"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_instagram">Instagram</Label>
                <Input
                  id="social_instagram"
                  value={data.social_instagram}
                  onChange={e => setData('social_instagram', e.target.value)}
                  placeholder="@handle or URL"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_facebook">Facebook</Label>
                <Input
                  id="social_facebook"
                  value={data.social_facebook}
                  onChange={e => setData('social_facebook', e.target.value)}
                  placeholder="username or URL"
                />
              </div>
            </CardContent>
          </Card>

          {/* Interests */}
          <Card>
            <CardHeader>
              <CardTitle>Interests</CardTitle>
              <CardDescription>
                Select up to {selectionLimit} causes and {selectionLimit} professional networks. Don't see one?
                Type your own — we'll review and add it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <TagCombobox
                label="Causes"
                placeholder="Search or add a cause..."
                suggestions={causeSuggestions}
                selected={data.interested_causes}
                onChange={(items) => setData('interested_causes', items.slice(0, selectionLimit))}
                allowCustom
                maxItems={selectionLimit}
                maxLength={customEntryLimit}
                error={errors.interested_causes}
              />
              <TagCombobox
                label="Professional Networks"
                placeholder="Search or add a network..."
                suggestions={networkSuggestions}
                selected={data.interested_industries}
                onChange={(items) => setData('interested_industries', items.slice(0, selectionLimit))}
                allowCustom
                maxItems={selectionLimit}
                maxLength={customEntryLimit}
                error={errors.interested_industries}
              />
            </CardContent>
          </Card>

          {/* Privacy */}
          <Card>
            <CardHeader>
              <CardTitle>Privacy</CardTitle>
              <CardDescription>Control what others can see</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="visibility">Show my profile publicly</Label>
                <Switch
                  id="visibility"
                  checked={data.visibility}
                  onCheckedChange={checked => setData('visibility', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="visibility_full_name">Show my full name</Label>
                <Switch
                  id="visibility_full_name"
                  checked={data.visibility_full_name}
                  onCheckedChange={checked => setData('visibility_full_name', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="visibility_email">Show my email</Label>
                <Switch
                  id="visibility_email"
                  checked={data.visibility_email}
                  onCheckedChange={checked => setData('visibility_email', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="business">I'm a business / professional</Label>
                <Switch
                  id="business"
                  checked={data.business}
                  onCheckedChange={checked => setData('business', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Email Preferences */}
          <Card>
            <CardHeader>
              <CardTitle>Updates from organizations you follow</CardTitle>
              <CardDescription>Choose how you want to hear about new posts.</CardDescription>
            </CardHeader>
            <CardContent>
              <fieldset
                className="grid grid-cols-1 gap-4 md:grid-cols-3"
                aria-label="Post email frequency"
              >
                {POST_EMAIL_OPTIONS.map(opt => {
                  const selected = data.post_email_frequency === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setData('post_email_frequency', opt.value)}
                      aria-pressed={selected}
                      className={`relative rounded-xl border p-4 text-left transition ${
                        selected
                          ? 'border-primary bg-primary/5 ring-2 ring-primary'
                          : 'border-black/10 bg-card hover:border-primary/40'
                      }`}
                    >
                      {selected && (
                        <span className="absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                      <p className="text-base font-semibold">{opt.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{opt.description}</p>
                    </button>
                  )
                })}
              </fieldset>
              <div className="mt-4 flex justify-end">
                <Button asChild variant="ghost" size="sm">
                  <Link href="/settings/muted-organizations">Manage muted organizations →</Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Connected Accounts */}
          <Card>
            <CardHeader>
              <CardTitle>Connected Accounts</CardTitle>
              <CardDescription>Link your Google account for one-click sign in</CardDescription>
            </CardHeader>
            <CardContent>
              {user.google_linked ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="size-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium">Google</p>
                      <p className="text-sm text-muted-foreground">Connected</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => router.delete('/settings/oauth/google_oauth2')}
                    disabled={!user.has_password}
                  >
                    Unlink
                  </Button>
                </div>
              ) : (
                <GoogleOAuthButton accountType="user" label="Link Google Account" mode="button" />
              )}
              {user.google_linked && !user.has_password && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Set a password before unlinking Google
                </p>
              )}
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Leave blank to keep your current password</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  value={data.password}
                  onChange={e => setData('password', e.target.value)}
                  autoComplete="new-password"
                />
                {errors.password && (
                  <p className="text-base text-destructive">{errors.password}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password_confirmation">Confirm new password</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={data.password_confirmation}
                  onChange={e => setData('password_confirmation', e.target.value)}
                  autoComplete="new-password"
                />
                {errors.password_confirmation && (
                  <p className="text-base text-destructive">{errors.password_confirmation}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Button type="submit" disabled={processing} className="w-full">
            Save Changes
          </Button>
        </form>
      </div>
    </>
  )
}
